import { useState } from 'react'
import { Trans } from '@lingui/macro'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CheckIcon from '@mui/icons-material/Check'

type LocalNetworkAccessDialogProps = {
  open: boolean
  onClose: () => void
}

const CHROME_FLAGS_URL = 'chrome://flags/#local-network-access-check'

export function LocalNetworkAccessDialog({ open, onClose }: LocalNetworkAccessDialogProps) {
  const [copied, setCopied] = useState(false)

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(CHROME_FLAGS_URL)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy URL:', err)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <WarningAmberIcon color="warning" />
        <Trans id="Accès au réseau local requis (Chrome)">Local network access required (Chrome)</Trans>
      </DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Trans id="dmp.localNetworkAccess.chromeBlockExplanation">
            Chrome blocks access to local network services. This security feature, called "Local Network Access", was introduced in Chrome 142.
          </Trans>
        </Alert>

        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          <Trans id="Comment autoriser l'accès au réseau local :">How to allow local network access:</Trans>
        </Typography>

        <List>
          <ListItem>
            <ListItemIcon>
              <CheckCircleOutlineIcon color="primary" />
            </ListItemIcon>
            <ListItemText
              primary={<Trans id="Option 1 : Autoriser via la demande de permission">Option 1: Allow through the permission prompt</Trans>}
              secondary={
                <span>
                  <Trans id="dmp.localNetworkAccess.permissionPromptExplanation">
                    Chrome displays a permission prompt when a website tries to access your local network. When you see "Allow local network access?", click "Allow".
                  </Trans>
                </span>
              }
            />
          </ListItem>

          <ListItem>
            <ListItemIcon>
              <CheckCircleOutlineIcon color="primary" />
            </ListItemIcon>
            <ListItemText
              primary={<Trans id="Option 2 : Réinitialiser la permission via les paramètres du site">Option 2: Reset permission in site settings</Trans>}
              secondary={
                <span>
                  <Trans id="Si vous ne voyez pas la demande ou si vous l'avez refusée par erreur :">If you do not see the prompt or denied it by mistake:</Trans>
                  <br />
                  <Trans id="1. Cliquez sur l'icône de cadenas/réglages dans la barre d'adresse">1. Click the site information icon next to the address bar</Trans>
                  <br />
                  <Trans id="dmp.localNetworkAccess.siteSettingsStep2">2. Click "Site settings"</Trans>
                  <br />
                  <Trans id="dmp.localNetworkAccess.siteSettingsStep3">3. Find "Local network access" and set it to "Allow"</Trans>
                </span>
              }
            />
          </ListItem>

          <ListItem>
            <ListItemIcon>
              <CheckCircleOutlineIcon color="primary" />
            </ListItemIcon>
            <ListItemText
              primary={<Trans id="Option 3 : Activer via les flags Chrome (avancé)">Option 3: Enable through Chrome flags (advanced)</Trans>}
              secondary={
                <span>
                  <Trans id="Si les options ci-dessus ne fonctionnent pas, vous pouvez activer la vérification via les flags Chrome :">If the options above do not work, enable the check through Chrome flags:</Trans>
                  <br />
                  <Trans id="Copiez et collez cette URL dans la barre d'adresse de Chrome :">Copy and paste this URL into the Chrome address bar:</Trans>
                  <Stack direction="row" alignItems="center" spacing={1} mt={1} mb={1}>
                    <code style={{ backgroundColor: '#f5f5f5', padding: '4px 8px', borderRadius: '4px' }}>{CHROME_FLAGS_URL}</code>
                    <Tooltip title={copied ? <Trans id="Copié !">Copied!</Trans> : <Trans id="Copier l'URL">Copy URL</Trans>}>
                      <IconButton size="small" onClick={handleCopyUrl} color={copied ? 'success' : 'default'}>
                        {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  </Stack>
                  <Trans id="dmp.localNetworkAccess.chromeFlagsInstruction">Set the flag to "Enabled (Blocking)" and restart Chrome.</Trans>
                </span>
              }
            />
          </ListItem>
        </List>

        <Alert severity="info" sx={{ mt: 2 }}>
          <Trans id="Après avoir autorisé l'accès, revenez sur cette page et la connexion devrait fonctionner automatiquement.">After allowing access, return to this page and the connection should work automatically.</Trans>
        </Alert>

        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary">
            <Trans id="En savoir plus sur <0>Chrome Local Network Access</0>">
              Learn more about{' '}
              <Link href="https://developer.chrome.com/blog/local-network-access" target="_blank" rel="noopener noreferrer">
                Chrome Local Network Access
              </Link>
            </Trans>
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ pt: 2 }}>
        <Button onClick={onClose} variant="contained">
          <Trans id="Fermer">Close</Trans>
        </Button>
      </DialogActions>
    </Dialog>
  )
}
